import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { Wishlist } from '@theme/wishlist';
import { formatMoney } from '@theme/money-formatting';
import { debounce } from '@theme/utilities';

/**
 * The global wishlist drawer — a single instance rendered once from
 * layout/theme.liquid (like #search-modal), addressed from anywhere via
 * `on:click="#wishlist-drawer/open"`. Bottom sheet on mobile, side panel on
 * desktop (see wishlist-drawer.liquid's stylesheet).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} emptyState
 * @property {HTMLElement} itemsList
 * @property {HTMLTemplateElement} rowTemplate
 * @property {HTMLTemplateElement} addToCartIcon
 * @property {HTMLElement} wishlistBubble
 * @property {HTMLElement} wishlistBubbleText
 * @property {HTMLElement} wishlistBubbleCount
 *
 * @extends {DialogComponent}
 */
class WishlistDrawerComponent extends DialogComponent {
  requiredRefs = [
    'dialog',
    'emptyState',
    'itemsList',
    'rowTemplate',
    'addToCartIcon',
    'wishlistBubble',
    'wishlistBubbleText',
    'wishlistBubbleCount',
  ];

  /** @type {Map<string, Promise<any>>} Per-open-lifetime product JSON fetch cache. */
  #productCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#handleWishlistUpdate);
    this.#renderList();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#handleWishlistUpdate);
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #handleWishlistUpdate = () => {
    this.#renderList();
  };

  /**
   * @param {string} url
   * @returns {Promise<any>}
   */
  #getProduct(url) {
    let promise = this.#productCache.get(url);
    if (!promise) {
      promise = fetch(`${url}.js`).then((response) => (response.ok ? response.json() : null));
      this.#productCache.set(url, promise);
    }
    return promise;
  }

  #renderList() {
    const items = Wishlist.getItems();

    this.refs.wishlistBubbleCount.textContent = items.length < 100 ? String(items.length) : '';
    this.refs.wishlistBubbleCount.classList.toggle('hidden', items.length === 0);
    this.refs.wishlistBubble.classList.toggle('visually-hidden', items.length === 0);
    this.refs.emptyState.hidden = items.length > 0;
    this.refs.itemsList.hidden = items.length === 0;
    this.refs.itemsList.replaceChildren();

    for (const item of items) {
      this.refs.itemsList.appendChild(this.#buildRow(item));
    }
  }

  /**
   * @param {import('@theme/wishlist').WishlistItem} item
   */
  #buildRow(item) {
    const fragment = /** @type {DocumentFragment} */ (this.refs.rowTemplate.content.cloneNode(true));
    const row = /** @type {HTMLElement} */ (fragment.querySelector('[data-wl-row]'));
    row.dataset.productId = item.id;

    const imageLink = row.querySelector('[data-wl-image-link]');
    const titleLink = row.querySelector('[data-wl-title-link]');
    if (imageLink instanceof HTMLAnchorElement) imageLink.href = item.url;
    if (titleLink instanceof HTMLAnchorElement) titleLink.href = item.url;

    const expandButton = row.querySelector('[data-wl-expand]');
    const expandedPanel = row.querySelector('[data-wl-expanded-panel]');
    expandButton?.addEventListener('click', () => {
      const isExpanded = row.classList.toggle('is-expanded');
      expandButton.setAttribute('aria-expanded', String(isExpanded));
      if (expandedPanel instanceof HTMLElement) expandedPanel.hidden = !isExpanded;
    });

    const removeButton = row.querySelector('[data-wl-remove]');
    removeButton?.addEventListener('click', () => Wishlist.remove(item.id));

    const noteInput = row.querySelector('[data-wl-note]');
    if (noteInput instanceof HTMLTextAreaElement) {
      noteInput.value = Wishlist.getNote(item.id);
      noteInput.addEventListener(
        'input',
        debounce(() => Wishlist.setNote(item.id, noteInput.value), 300)
      );
    }

    this.#getProduct(item.url).then((product) => {
      if (!product) {
        row.remove();
        return;
      }
      this.#populateRow(row, product);
    });

    return fragment;
  }

  /**
   * @param {HTMLElement} row
   * @param {any} product
   */
  #populateRow(row, product) {
    const image = row.querySelector('[data-wl-image]');
    const vendor = row.querySelector('[data-wl-vendor]');
    const title = row.querySelector('[data-wl-title]');
    const price = row.querySelector('[data-wl-price]');
    const variantsContainer = row.querySelector('[data-wl-variants]');

    if (image instanceof HTMLImageElement && product.featured_image) {
      image.src = product.featured_image;
      image.alt = product.title ?? '';
    }
    if (vendor) vendor.textContent = product.vendor ?? '';
    if (title) title.textContent = product.title ?? '';

    // The "starting from" price line is only shown for single-variant
    // products (where the variant list below is skipped for a plain Add to
    // Cart button instead) — otherwise the variant rows already show every
    // price themselves and this line is just a redundant duplicate.
    const variantCount = Array.isArray(product.variants) ? product.variants.length : 0;
    if (price instanceof HTMLElement) {
      price.hidden = variantCount > 1;
      if (variantCount <= 1) price.textContent = this.#formatMoney(product.price_min);
    }

    if (variantsContainer instanceof HTMLElement) this.#buildVariantArea(variantsContainer, product);
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    return formatMoney(cents, format, currency);
  }

  /**
   * Builds an add-to-cart button using the same icon the theme's own
   * add-to-cart button uses (cloned from a <template> rendered once in
   * Liquid, since this static JS file can't call inline_asset_content
   * itself).
   * @param {string} extraClass
   */
  #buildAddToCartButton(extraClass) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `wishlist-drawer__atc-btn ${extraClass}`;
    button.appendChild(this.refs.addToCartIcon.content.cloneNode(true));
    return button;
  }

  /**
   * Renders either a single full-width "Add to Cart" button (one variant)
   * or a list of variant rows — variant info on the left, a dedicated
   * icon-only add-to-cart button on the right for each — when a product
   * has multiple variants.
   * @param {HTMLElement} container
   * @param {any} product
   */
  #buildVariantArea(container, product) {
    container.replaceChildren();
    const variants = Array.isArray(product.variants) ? product.variants : [];

    if (variants.length <= 1) {
      const variant = variants[0];
      if (!variant) return;

      const button = this.#buildAddToCartButton('wishlist-drawer__atc-btn--full');
      const label = document.createElement('span');
      label.textContent = this.dataset.atcLabel || 'Add to Cart';
      button.appendChild(label);
      button.disabled = !variant.available;
      button.addEventListener('click', () => this.#addToCart(variant.id, button));
      container.appendChild(button);
      return;
    }

    variants.forEach((variant) => {
      container.appendChild(this.#buildVariantRow(variant));
    });
  }

  /**
   * @param {any} variant
   */
  #buildVariantRow(variant) {
    const row = document.createElement('div');
    row.className = 'wishlist-drawer__variant-row';
    if (!variant.available) row.classList.add('is-unavailable');

    const info = document.createElement('div');
    info.className = 'wishlist-drawer__variant-info';

    const label = document.createElement('span');
    label.className = 'wishlist-drawer__variant-label';
    label.textContent = variant.title;
    info.appendChild(label);

    const priceGroup = document.createElement('span');
    priceGroup.className = 'wishlist-drawer__variant-price-group';

    const ppm = this.#computePricePerMl(variant.title, variant.price);
    if (ppm) {
      const ppmEl = document.createElement('span');
      ppmEl.className = 'wishlist-drawer__variant-ppm';
      ppmEl.textContent = `${ppm}/ml`;
      priceGroup.appendChild(ppmEl);
    }

    const priceEl = document.createElement('span');
    priceEl.className = 'wishlist-drawer__variant-price';
    priceEl.textContent = this.#formatMoney(variant.price);
    priceGroup.appendChild(priceEl);

    info.appendChild(priceGroup);
    row.appendChild(info);

    const addButton = this.#buildAddToCartButton('wishlist-drawer__atc-btn--icon-only');
    addButton.setAttribute('aria-label', this.dataset.atcLabel || 'Add to Cart');
    addButton.disabled = !variant.available;
    if (variant.available) {
      addButton.addEventListener('click', () => this.#addToCart(variant.id, addButton));
    }
    row.appendChild(addButton);

    return row;
  }

  /**
   * Pure JS port of product-card.liquid's price-per-ml Liquid logic — keep
   * in sync if that parsing logic ever changes.
   * @param {string} variantTitle
   * @param {number} priceCents
   */
  #computePricePerMl(variantTitle, priceCents) {
    const title = (variantTitle || '').toLowerCase().trim();
    const mlMatch = title.match(/([\d.]+)\s*ml/);
    if (!mlMatch) return '';

    const mlPortion = title.split('ml')[0].trim();
    let ml = parseFloat(mlPortion.split(' ').pop() || '0');
    let qty = 1;

    if (mlPortion.includes('x')) {
      const qtyPart = parseFloat(mlPortion.split('x')[0].trim());
      if (qtyPart > 1) qty = qtyPart;
    }

    const totalMl = ml * qty;
    if (!totalMl) return '';

    return this.#formatMoney(Math.round(priceCents / totalMl));
  }

  /**
   * @param {number} variantId
   * @param {HTMLButtonElement} button
   */
  #addToCart(variantId, button) {
    button.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          button.disabled = false;
          return;
        }
        return fetch('/cart.js')
          .then((response) => response.json())
          .then((cart) => {
            document.dispatchEvent(new CartAddEvent(cart, String(variantId), { itemCount: cart.item_count }));
            this.close();
          });
      })
      .catch(() => {
        button.disabled = false;
      });
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
